import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
  Heart, Stethoscope, Scale, Newspaper, BookOpen, MapPin, MessageCircle, Route,
  Lock, EyeOff, KeyRound, Trash2, Server, ShieldCheck, ArrowRight, Info,
} from 'lucide-react';
import { LotusLogo, BloomWordmark, LOGO_INK } from '../components/LotusLogo';
import { API } from '../lib/api';
import { useJazyk } from '../context/LanguageContext';
import LanguageSwitch from '../components/world/LanguageSwitch';

const SECTIONS = [
  { icon: Heart, titulek: 'nav.support', text: 'uvod.karta.podpora' },
  { icon: Stethoscope, titulek: 'nav.specialists', text: 'uvod.karta.odbornici' },
  { icon: Route, titulek: 'uvod.karta.pruvodceNazev', text: 'uvod.karta.pruvodce' },
  { icon: Scale, titulek: 'nav.legal', text: 'uvod.karta.pravo' },
  { icon: BookOpen, titulek: 'nav.stories', text: 'uvod.karta.zkusenosti' },
  { icon: Newspaper, titulek: 'nav.news', text: 'uvod.karta.aktuality' },
  { icon: MapPin, titulek: 'nav.nearby', text: 'uvod.karta.okoli' },
  { icon: MessageCircle, titulek: 'uvod.karta.zpravyNazev', text: 'uvod.karta.zpravy' },
];

const SECURITY = [
  { icon: EyeOff, titulek: 'uvod.bezp.zvencinic', text: 'uvod.bezp.zvencinicText' },
  { icon: Lock, titulek: 'uvod.bezp.sifrovani', text: 'uvod.bezp.sifrovaniText' },
  { icon: KeyRound, titulek: 'uvod.bezp.hesla', text: 'uvod.bezp.heslaText' },
  { icon: ShieldCheck, titulek: 'uvod.bezp.kontakty', text: 'uvod.bezp.kontaktyText' },
  { icon: Trash2, titulek: 'uvod.bezp.smazane', text: 'uvod.bezp.smazaneText' },
  { icon: Server, titulek: 'uvod.bezp.server', text: 'uvod.bezp.serverText' },
];

const LandingPage = () => {
  const { t } = useJazyk();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    axios.get(`${API}/stats`).then((r) => setStats(r.data)).catch(() => {});
  }, []);

  const statItems = stats ? [
    { value: stats.members, label: t('auth.clenu', { pocet: stats.members }) },
    { value: stats.experiences, label: t('auth.prispevku', { pocet: stats.experiences }) },
    { value: stats.specialists, label: t('auth.odborniku', { pocet: stats.specialists }) },
    { value: stats.services, label: t('auth.nabidek', { pocet: stats.services }) },
  ] : [];

  return (
    <div className="bg-bloom-bg" data-testid="landing-page">
      <div className="pride-bar fixed top-0 left-0 right-0 z-50" />

      <header className="sticky top-0 z-40 glass border-b border-border/40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <LotusLogo size={34} />
            <span className="font-heading text-lg font-bold" style={{ color: LOGO_INK }}>Bloom</span>
          </div>
          <div className="flex items-center gap-2">
            <LanguageSwitch />
            <Link
              to="/auth"
              className="text-sm font-medium text-bloom-sub hover:text-bloom-violet px-3 py-2 rounded-lg transition-colors"
              data-testid="landing-login-link"
            >
              {t('uvod.prihlasit')}
            </Link>
            <Link
              to="/auth?tab=register"
              className="text-sm font-semibold text-white bg-bloom-violet hover:bg-bloom-violet/90 px-4 py-2 rounded-xl transition-colors shadow-sm"
              data-testid="landing-register-link"
            >
              {t('uvod.vytvoritUcet')}
            </Link>
          </div>
        </div>
      </header>

      {/* Úvod */}
      <section className="relative overflow-hidden">
        {/* Jemné barevné plochy na pozadí – dekorace, proto aria-hidden */}
        <div aria-hidden="true" className="pointer-events-none absolute inset-0">
          <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-bloom-pride-blue/15 blur-3xl" />
          <div className="absolute top-10 -right-24 w-96 h-96 rounded-full bg-bloom-pride-pink/20 blur-3xl" />
          <div className="absolute bottom-0 left-1/3 w-80 h-80 rounded-full bg-bloom-violet/10 blur-3xl" />
        </div>

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 pt-14 pb-16 sm:pt-20 sm:pb-20 text-center">
          <BloomWordmark size={116} className="mb-7" />

          <h1 className="font-heading text-3xl sm:text-5xl font-extrabold text-bloom-text leading-tight">
            {t('uvod.nadpis')}
          </h1>
          <p className="mt-5 text-base sm:text-lg text-bloom-sub max-w-2xl mx-auto leading-relaxed">
            {t('uvod.podnadpis')}
          </p>

          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/auth?tab=register"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 text-base font-semibold text-white bg-bloom-violet hover:bg-bloom-violet/90 px-7 py-3.5 rounded-2xl transition-colors shadow-md"
              data-testid="hero-register-btn"
            >
              {t('uvod.vytvoritUcet')} <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/auth"
              className="w-full sm:w-auto inline-flex items-center justify-center text-base font-medium text-bloom-violet bg-white hover:bg-bloom-violet/5 border border-bloom-violet/25 px-7 py-3.5 rounded-2xl transition-colors"
              data-testid="hero-login-btn"
            >
              {t('uvod.uzMamUcet')}
            </Link>
          </div>

          <p className="mt-5 text-sm text-bloom-sub/80 flex items-center justify-center gap-1.5">
            <Lock className="w-3.5 h-3.5" />
            {t('uvod.zdarma')}
          </p>

          {statItems.length > 0 && (
            <div className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-2xl mx-auto" data-testid="landing-stats">
              {statItems.map((s) => (
                <div key={s.label} className="bg-white/70 border border-border/40 rounded-2xl py-4">
                  <div className="font-heading text-2xl font-bold text-bloom-violet">{s.value}</div>
                  <div className="text-xs text-bloom-sub mt-0.5">{s.label}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* K čemu Bloom slouží */}
      <section className="bg-white border-y border-border/40">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-bloom-text text-center">
            {t('uvod.kCemuJe')}
          </h2>
          <div className="mt-6 space-y-4 text-bloom-sub leading-relaxed text-[15px] sm:text-base">
            <p>
              {t('uvod.kCemuJe1')}
            </p>
            <p>
              {t('uvod.kCemuJe2')}
            </p>
            <p>
              {t('uvod.kCemuJe3')}
            </p>
          </div>
        </div>
      </section>

      {/* Fotka mezi sekcemi – barvy trans vlajky jsou zároveň barvy Bloomu.

          Výška se počítá z šířky, ne pevně. `object-cover` ořezává tu stranu,
          která přetéká: při pevné výšce byl pás na širokém monitoru až 4,8:1,
          fotka má 4:1, a ukrojilo se skoro půl výšky – tedy přesně modré pruhy
          nahoře a dole. Trans vlajka bez modré není trans vlajka.

          Poměr pásu proto nikdy nesmí být širší než fotka. Užší vadí: ořezává
          se asfalt po stranách, kde nic není. */}
      <section aria-hidden="true" className="relative">
        <img
          src="/assets/foto/trans-vlajka.jpg"
          alt=""
          width="2480"
          height="620"
          loading="lazy"
          className="w-full aspect-[2/1] sm:aspect-[3/1] lg:aspect-[4/1] object-cover"
        />
        {/* Přechody nahoře a dole, ať fotka nevypadá nalepená */}
        <div className="pointer-events-none absolute inset-x-0 top-0 h-10 bg-gradient-to-b from-white to-transparent" />
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-bloom-bg to-transparent" />
      </section>
      <p className="text-center text-[11px] text-bloom-sub/50 pt-2">Foto: Katie Rainbow / Pexels</p>

      {/* Co Bloom nabízí */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
        <h2 className="font-heading text-2xl sm:text-3xl font-bold text-bloom-text text-center">
          {t('uvod.coNajdes')}
        </h2>
        <p className="mt-3 text-center text-bloom-sub max-w-xl mx-auto">
          {t('uvod.coNajdesPopis')}
        </p>

        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {SECTIONS.map(({ icon: Icon, titulek, text }) => (
            <div
              key={titulek}
              className="bg-white border border-border/40 rounded-2xl p-5 hover:border-bloom-violet/30 hover:shadow-sm transition-all"
            >
              <div className="w-10 h-10 rounded-xl bg-bloom-violet/10 flex items-center justify-center">
                <Icon className="w-5 h-5 text-bloom-violet" />
              </div>
              <h3 className="mt-3.5 font-heading font-bold text-bloom-text">{t(titulek)}</h3>
              <p className="mt-1.5 text-sm text-bloom-sub leading-relaxed">{t(text)}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Zabezpečení */}
      <section className="bg-white border-y border-border/40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
          <div className="text-center max-w-2xl mx-auto">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-bloom-mint/15 text-[13px] font-medium text-bloom-text">
              <ShieldCheck className="w-4 h-4 text-bloom-violet" />
              {t('uvod.soukromiZaklad')}
            </div>
            <h2 className="mt-4 font-heading text-2xl sm:text-3xl font-bold text-bloom-text">
              {t('uvod.jakZabezpeceny')}
            </h2>
            <p className="mt-3 text-bloom-sub">
              {t('uvod.zabezpeceniPopis')}
            </p>
          </div>

          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {SECURITY.map(({ icon: Icon, titulek, text }) => (
              <div key={titulek} className="bg-bloom-bg border border-border/40 rounded-2xl p-5">
                <div className="w-10 h-10 rounded-xl bg-white border border-border/40 flex items-center justify-center">
                  <Icon className="w-5 h-5 text-bloom-violet" />
                </div>
                <h3 className="mt-3.5 font-heading font-bold text-bloom-text">{t(titulek)}</h3>
                <p className="mt-1.5 text-sm text-bloom-sub leading-relaxed">{t(text)}</p>
              </div>
            ))}
          </div>

          {/* Poctivé přiznání hranic – ať se na to nikdo nespoléhá víc, než může */}
          <div className="mt-8 max-w-3xl mx-auto bg-amber-50/70 border border-amber-200/70 rounded-2xl p-5 sm:p-6">
            <div className="flex gap-3">
              <Info className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
              <div>
                <h3 className="font-heading font-bold text-bloom-text">{t('uvod.coNeumi')}</h3>
                <p className="mt-1.5 text-sm text-bloom-sub leading-relaxed">
                  {t('uvod.coNeumiA')} <strong>{t('uvod.metadataTucne')}</strong>{t('uvod.coNeumiB')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Závěrečná výzva */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 py-16 sm:py-20 text-center">
        <LotusLogo size={64} className="mx-auto" />
        <h2 className="mt-5 font-heading text-2xl sm:text-3xl font-bold text-bloom-text">
          {t('uvod.pridejSe')}
        </h2>
        <p className="mt-3 text-bloom-sub max-w-xl mx-auto leading-relaxed">
          {t('uvod.pridejSePopis')}
        </p>
        <Link
          to="/auth?tab=register"
          className="mt-7 inline-flex items-center justify-center gap-2 text-base font-semibold text-white bg-bloom-violet hover:bg-bloom-violet/90 px-7 py-3.5 rounded-2xl transition-colors shadow-md"
          data-testid="footer-register-btn"
        >
          {t('uvod.vytvoritUcet')} <ArrowRight className="w-4 h-4" />
        </Link>
      </section>
    </div>
  );
};

export default LandingPage;
